import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Search, ArrowLeft, Loader2, Send, Camera, Gamepad2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

function RequestForm({ slug, tables }) {
  const [f, setF] = useState({ name: "", kind: "sit_with", sit_with: "", table_id: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  async function send(e) {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/public/weddings/${slug}/seating/requests`, f); setDone(true); toast.success("Request sent to the couple"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  if (done) return <p className="text-center text-sm text-[var(--muted)] py-6" data-testid="seat-request-done">Thank you! The couple will look at your request.</p>;
  return (
    <form onSubmit={send} className="bg-white border border-[var(--line)] rounded-2xl p-5 space-y-3" data-testid="seat-request-form">
      <p className="font-serif text-2xl">Request a seat change</p>
      <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your full name (as on your invitation)" className="input-luxe" data-testid="seat-req-name" />
      <div className="grid grid-cols-2 gap-2">
        {[["sit_with", "Sit with someone"], ["move", "Move to a table"]].map(([v, l]) => (
          <button type="button" key={v} onClick={() => setF({ ...f, kind: v })} className={`py-2 rounded-full text-xs uppercase tracking-wider ${f.kind === v ? "bg-[var(--ink)] text-white" : "border border-[var(--line)] text-[var(--muted)]"}`} data-testid={`seat-req-kind-${v}`}>{l}</button>
        ))}
      </div>
      {f.kind === "sit_with" && <input required value={f.sit_with} onChange={(e) => setF({ ...f, sit_with: e.target.value })} placeholder="Who would you like to sit with?" className="input-luxe" data-testid="seat-req-with" />}
      <select value={f.table_id} onChange={(e) => setF({ ...f, table_id: e.target.value })} required={f.kind === "move"} className="input-luxe" data-testid="seat-req-table">
        <option value="">{f.kind === "move" ? "Pick a table…" : "Any table (optional)"}</option>
        {tables.map((t) => <option key={t.table_id} value={t.table_id}>{t.name}</option>)}
      </select>
      <textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={300} rows={2} placeholder="A note for the couple (optional)" className="input-luxe resize-none" data-testid="seat-req-note" />
      <button disabled={busy} className="btn-gold w-full flex items-center justify-center gap-2" data-testid="seat-req-send"><Send className="w-4 h-4" /> {busy ? "Sending…" : "Send request"}</button>
    </form>
  );
}

export default function PublicSeating() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const focus = params.get("table");
  const [hasGames, setHasGames] = useState(false);
  useEffect(() => { api.get(`/public/weddings/${slug}/games`).then((r) => setHasGames(r.data.length > 0)).catch(() => {}); }, [slug]);
  useEffect(() => { api.get(`/public/weddings/${slug}/seating`).then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e))); }, [slug]);
  if (err) return <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center"><p className="font-serif text-2xl" data-testid="seating-error">{err}</p><Link to={`/site/${slug}`} className="text-[var(--gold)]">Back to the wedding site</Link></div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>;
  const needle = q.trim().toLowerCase();
  const tables = d.tables.map((t) => ({ ...t, hit: (needle && t.guests.some((g) => g.toLowerCase().includes(needle))) || (!needle && t.table_id === focus) }))
    .filter((t) => !needle || t.hit || t.name.toLowerCase().includes(needle))
    .sort((a, b) => (b.table_id === focus) - (a.table_id === focus));
  return (
    <div className="min-h-screen bg-[var(--bg-warm)] pb-24" data-testid="public-seating">
      <div className="max-w-4xl mx-auto px-5 pt-8">
        <Link to={`/site/${slug}`} className="text-sm text-[var(--muted)] flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> {d.couple_names}</Link>
        <p className="font-script text-[var(--gold)] text-4xl mt-6">Find your table</p>
        <h1 className="font-serif font-light text-4xl sm:text-5xl">Seating chart</h1>
        <div className="flex gap-2 mt-5">
          <Link to={`/site/${slug}/social`} className="btn-gold !py-2 text-sm flex items-center gap-2" data-testid="seating-photo-wall"><Camera className="w-4 h-4" /> Live photo wall</Link>
          {hasGames && <Link to={`/site/${slug}#games`} className="btn-outline-gold !py-2 text-sm flex items-center gap-2" data-testid="seating-games"><Gamepad2 className="w-4 h-4" /> Party games</Link>}
        </div>
        <div className="relative mt-6">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your name" className="input-luxe !pl-10" data-testid="seating-search" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="seating-tables">
          {tables.map((t) => (
            <div key={t.table_id} className={`bg-white rounded-2xl p-5 border ${t.hit ? "border-[var(--gold)] shadow-md" : "border-[var(--line)]"}`} data-testid={`public-table-${t.table_id}`}>
              <p className="font-serif text-2xl">{t.name}</p>
              <ul className="mt-2 space-y-1 text-sm">{t.guests.map((g) => <li key={g} className={needle && g.toLowerCase().includes(needle) ? "text-[var(--gold)] font-medium" : ""}>{g}</li>)}</ul>
              {!t.guests.length && <p className="text-xs text-[var(--muted)]">No one seated yet</p>}
            </div>
          ))}
          {!tables.length && <p className="text-[var(--muted)] col-span-full text-center py-8" data-testid="seating-no-match">No match. Check the spelling, or ask the couple below.</p>}
        </div>
        <div className="max-w-md mx-auto mt-12"><RequestForm slug={slug} tables={d.tables} /></div>
      </div>
    </div>
  );
}
